import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import { getDateFnsLocale, parseDateKey } from '@/domain/dates';
import { filterPlans, groupPlansByDate } from '@/domain/plans';
import { PlanChip } from '@/features/plans/PlanChip';
import styles from './PlanListView.module.css';

export function PlanListView() {
  const { t, i18n } = useTranslation();
  const session = useAppStore((s) => s.session);
  const plans = useAppStore((s) => s.plans);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);
  const cursorMonth = useAppStore((s) => s.cursorMonth);

  const locale = preferences?.locale ?? (i18n.language.startsWith('ru') ? 'ru' : 'en');
  const viewerId = session?.user.id ?? '';
  const dateFnsLocale = getDateFnsLocale(locale);

  const monthPlans = useMemo(() => {
    if (!viewerId) return [];
    return filterPlans(
      plans.filter(
        (p) => !p.deletedAt && p.planDate && p.planDate.startsWith(`${cursorMonth}-`),
      ),
      filters,
      viewerId,
    );
  }, [plans, filters, viewerId, cursorMonth]);

  const grouped = useMemo(() => groupPlansByDate(monthPlans), [monthPlans]);
  const sortedKeys = useMemo(
    () => Array.from(grouped.keys()).sort((a, b) => a.localeCompare(b)),
    [grouped],
  );

  if (sortedKeys.length === 0) {
    return <p className={styles.empty}>{t('common.empty')}</p>;
  }

  return (
    <div className={styles.list}>
      {sortedKeys.map((dateKey) => {
        const items = grouped.get(dateKey) ?? [];
        const label = format(parseDateKey(dateKey), 'EEEE, d MMMM', {
          locale: dateFnsLocale,
        });

        return (
          <section key={dateKey} className={styles.group}>
            <h3 className={styles.groupHeader}>{label}</h3>
            <div className={styles.plans}>
              {items.map((plan) => (
                <PlanChip key={plan.id} plan={plan} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
